import { useEffect, useMemo, useRef, useState } from "react";
import { Mail, MessageSquare, Reply, AlertTriangle, Loader2, PauseCircle, PlayCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_BADGES = {
  delivered: { bg: "bg-emerald-500/15", text: "text-emerald-300", label: "Delivered" },
  opened:    { bg: "bg-teal-500/15",    text: "text-teal-300",    label: "Opened" },
  clicked:   { bg: "bg-sky-500/15",     text: "text-sky-300",     label: "Clicked" },
  replied:   { bg: "bg-emerald-600/25", text: "text-emerald-200 font-semibold", label: "Replied ↩" },
  bounced:   { bg: "bg-rose-500/15",    text: "text-rose-300",    label: "Bounced" },
  failed:    { bg: "bg-rose-500/15",    text: "text-rose-300",    label: "Failed" },
  sent:      { bg: "bg-slate-500/15",   text: "text-slate-300",   label: "Sent" },
};

function _relTime(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diffSec = (Date.now() - dt.getTime()) / 1000;
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    const d = new Date();
    const yesterday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
    if (dt >= yesterday && dt < new Date(d.getFullYear(), d.getMonth(), d.getDate())) {
      return `Yesterday at ${dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    }
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return dt.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const style = STATUS_BADGES[status] || STATUS_BADGES.sent;
  return (
    <span
      data-testid={`activity-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] tracking-wide ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
}

function StepIcon({ channel: _channel }) {
  // Text-message outreach disabled platform-wide (BLOCK 56 A.2). All timeline rows are email.
  return <Mail className="w-4 h-4 text-emerald-400" />;
}

function ReplyModal({ open, message, onClose, ownerEmail }) {
  if (!open || !message) return null;
  const body = message.reply?.body || "";
  const mailto = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent("Re: " + (message.subject || ""))}`
    : "";
  return (
    <div
      data-testid="reply-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ae-card w-full max-w-2xl p-6 mx-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg text-slate-100">Reply from owner</h3>
            <p className="text-xs text-slate-400 mt-1">
              Received {_relTime(message.reply?.received_at)} · replying to <span className="text-slate-200">{message.subject || "(no subject)"}</span>
            </p>
          </div>
          <button
            data-testid="reply-modal-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100"
          >
            Close
          </button>
        </div>
        <div className="whitespace-pre-wrap text-sm text-slate-200 bg-slate-900/40 border border-slate-800 rounded-md p-4 leading-relaxed">
          {body || <span className="text-slate-500 italic">Reply body not captured. Check Brevo inbound webhook logs.</span>}
        </div>
        {mailto && (
          <a
            href={mailto}
            data-testid="reply-modal-reply-now"
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-950 text-sm font-medium"
          >
            <Reply className="w-4 h-4" /> Reply now
          </a>
        )}
      </div>
    </div>
  );
}

export default function AutoOutreachActivityPanel({ opp, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const [toggling, setToggling] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const pollRef = useRef(null);

  const oid = opp?.oid;

  const load = async () => {
    if (!oid) return;
    try {
      const r = await api.get(`/opportunities/${oid}/outreach-activity`, { params: { limit: visibleCount } });
      setData(r.data);
    } catch (err) {
      // Silent ,  panel just shows empty state
    }
  };

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [oid, visibleCount]);

  // 30-second polling, paused when tab hidden
  useEffect(() => {
    if (!oid) return;
    const startPoll = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        if (!document.hidden) load();
      }, 30000);
    };
    const onVis = () => {
      if (document.hidden) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } else {
        load();
        startPoll();
      }
    };
    startPoll();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line
  }, [oid, visibleCount]);

  const toggleBlocked = async () => {
    if (!oid) return;
    const next = !(data?.outreach_blocked);
    setToggling(true);
    try {
      await api.patch(`/opportunities/${oid}`, { outreach_blocked: next });
      setData((cur) => (cur ? { ...cur, outreach_blocked: next } : cur));
      toast.success(next ? "Auto-outreach paused for this opportunity" : "Auto-outreach resumed");
      if (onUpdate) onUpdate({ ...opp, outreach_blocked: next });
    } catch (e) {
      toast.error("Failed to update outreach setting");
    } finally {
      setToggling(false);
    }
  };

  const scrollToContact = () => {
    const el = document.querySelector('[data-testid="owner-contact-panel"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const messages = data?.messages || [];
  const summary = data?.summary || { emails_sent: 0, open_rate: 0, reply_rate: 0 };
  const isPaid = useMemo(() => {
    const t = (opp?.tier_hint || "").toLowerCase();
    // Best-effort: rely on backend to gate; we render optimistic upgrade only when explicitly free.
    return t !== "free";
  }, [opp]);

  if (!opp) return null;

  return (
    <div className="ae-card p-5" data-testid="auto-outreach-activity-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-lg text-slate-100 flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" /> Approval-Gated Outreach Activity
          </h3>
          <p className="text-xs text-slate-400 mt-1">Sequences that fired after you approved them from the Ready-to-Contact queue.</p>
        </div>
        <button
          data-testid="auto-outreach-toggle"
          onClick={toggleBlocked}
          disabled={toggling}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
            data?.outreach_blocked
              ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15"
              : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
          }`}
        >
          {data?.outreach_blocked ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
          Auto-Outreach {data?.outreach_blocked ? "OFF" : "ON"}
        </button>
      </div>

      {data?.outreach_blocked && (
        <div data-testid="auto-outreach-paused-banner" className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Outreach paused for this opportunity. All sequences stopped. Toggle back on to resume.
        </div>
      )}

      {/* iter58: also flag that this section is subscriber-scoped */}
      {!data?.outreach_blocked && summary.emails_sent === 0 && (
        <div className="mb-4 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 text-xs text-slate-400">
          Nothing has fired yet. When you approve this opportunity from the Ready-to-Contact queue, activity will appear here.
        </div>
      )}

      {/* Summary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <MetricChip label="Emails Sent" value={summary.emails_sent} testid="metric-emails-sent" />
        <MetricChip label="Open Rate" value={`${summary.open_rate ?? 0}%`} testid="metric-open-rate" />
        <MetricChip label="Reply Rate" value={`${summary.reply_rate ?? 0}%`} testid="metric-reply-rate" />
      </div>

      {/* Timeline / empty state */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-8 text-sm text-slate-400 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
        </div>
      ) : messages.length === 0 ? (
        <EmptyState data={data} opp={opp} />
      ) : (
        <div className="space-y-2" data-testid="activity-timeline">
          {messages.map((m) => (
            <TimelineRow
              key={m.smid}
              msg={m}
              onViewReply={() => setReplyOpen(m)}
              onFixContact={scrollToContact}
            />
          ))}
          {data?.has_more && (
            <button
              data-testid="load-more-activity"
              onClick={() => setVisibleCount((n) => n + 20)}
              className="w-full mt-2 px-3 py-2 text-xs text-slate-300 border border-slate-800 rounded-md hover:bg-slate-900/40"
            >
              Load more
            </button>
          )}
        </div>
      )}

      <ReplyModal
        open={!!replyOpen}
        message={replyOpen}
        onClose={() => setReplyOpen(null)}
        ownerEmail={opp?.owner_email}
      />
    </div>
  );
}

function MetricChip({ label, value, testid }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-100 leading-tight mt-1">{value}</div>
    </div>
  );
}

function TimelineRow({ msg, onViewReply, onFixContact }) {
  const label = `Email ${msg.step || 1}`;
  const target = msg.owner_email_masked;

  return (
    <div className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-slate-900/30" data-testid={`activity-row-${msg.smid}`}>
      <div className="mt-0.5"><StepIcon channel={msg.channel} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm text-slate-100 font-medium">{label}</span>
          <span className="text-xs text-slate-400 truncate">to {target || ", "}</span>
          <StatusBadge status={msg.status} />
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{_relTime(msg.sent_at)}</div>
        {msg.status === "replied" && (
          <button
            data-testid={`view-reply-${msg.smid}`}
            onClick={onViewReply}
            className="mt-1 text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1"
          >
            <Reply className="w-3 h-3" /> View reply →
          </button>
        )}
        {(msg.status === "bounced" || msg.status === "failed") && (
          <button
            data-testid={`fix-contact-${msg.smid}`}
            onClick={onFixContact}
            className="mt-1 text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
          >
            <AlertTriangle className="w-3 h-3" /> Fix contact →
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ data, opp }) {
  const score = data?.cf_readiness_score ?? opp?.cf_readiness_score ?? 0;
  const auto = data?.outreach_auto_triggered;
  // Iter 53: subscriber has no complete sender profile → block-with-CTA
  // takes precedence over all other empty states, because preflight
  // will refuse every send until this is resolved.
  const senderReady = data?.sender_profile_ready !== false;
  if (auto) {
    return (
      <div data-testid="activity-empty-firing" className="flex items-center gap-2 py-6 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Outreach sequence started. First email delivering now…
      </div>
    );
  }
  if (!senderReady) {
    return (
      <div data-testid="activity-empty-sender-profile-incomplete" className="py-6 text-sm text-amber-300 flex items-center gap-2">
        <span>Complete your sender profile to activate outreach</span>
        <a href="/app/settings" data-testid="activity-open-settings" className="underline text-amber-200 hover:text-amber-100 font-medium">Open Settings →</a>
      </div>
    );
  }
  if (score < 70) {
    return (
      <div data-testid="activity-empty-below-threshold" className="py-6 text-sm text-slate-400">
        Outreach will start automatically when this opportunity scores 70 or above.
      </div>
    );
  }
  return (
    <div data-testid="activity-empty-upgrade" className="py-6 text-sm text-slate-300 flex items-center gap-2">
      <span>Upgrade to Pro to activate automated outreach</span>
      <a href="/pricing" className="text-emerald-300 hover:text-emerald-200 font-medium">→</a>
    </div>
  );
}
